// SubjectCoefficientPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Alert, Table, Tag, Button, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import PageHeader from "components/Common/PageHeader";
import CommonSelect from "components/Common/CommonSelect";
import { ClassLevel } from "types/classLevelTypes";
import { CLASS_SECTION_OPTIONS_BY_LEVEL } from "../../../constants/classConstants";
import useSubjectCoefficients from "../hooks/useSubjectCoefficients";
import Loader from "components/Common/loader";
import AddEditSubjectCoefficientModal from "../modals/AddEditSubjectCoefficientsModal";
import NoDataComponent from "components/Common/NoData";
import ExpandedContent from "../components/ExpandedContent";
import { getClassLevelLabelFromCode, groupByBranch } from "utils/schoolUtils";
import SettingsLayout from "../components/SettingsLayout";
// import { groupByBranch } from "./utils/subjectGrouping";

const SubjectCoefficientPage: React.FC = () => {
  const { t } = useTranslation();

  // cycle filter
  const cycleOptions = useMemo(() => CLASS_SECTION_OPTIONS_BY_LEVEL(t), [t]);
  const [selectedCycle, setSelectedCycle] = useState<string | undefined>(cycleOptions[0]?.value);

  // data hook
  const {
    classLevels,
    loading,
    error,
    fetchSubjectCoefficients,
    saveSubjectCoefficient,
    removeSubjectCoefficient,
  } = useSubjectCoefficients();

  // modal state
  const [currentClassLevel, setCurrentClassLevel] = useState<ClassLevel | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // fetch on mount & cycle change
  useEffect(() => {
    if (selectedCycle) fetchSubjectCoefficients({ schoolCycle: selectedCycle });
  }, [selectedCycle]);

  const openAddModal = useCallback((cl: ClassLevel) => {
    setCurrentClassLevel(cl);
    setIsModalOpen(true);
  }, []);

  const handleSave = useCallback(
    async (payload: any) => {
      await saveSubjectCoefficient(payload);
      if (selectedCycle) fetchSubjectCoefficients({ schoolCycle: selectedCycle });
    },
    [saveSubjectCoefficient, fetchSubjectCoefficients, selectedCycle]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await removeSubjectCoefficient(id);
      if (selectedCycle) fetchSubjectCoefficients({ schoolCycle: selectedCycle });
    },
    [removeSubjectCoefficient, fetchSubjectCoefficients, selectedCycle]
  );

 

  // columns
  const columns: ColumnsType<ClassLevel> = useMemo(
    () => [
      {
        title: t("class_level"),
        dataIndex: "name",
        key: "name",
        render: (text: string, record) => (
          <Space direction="vertical" size={0}>
            <span className="fw-600">{getClassLevelLabelFromCode(record.labelKey)}</span>
            <span className="text-muted fs-12">{t(record.schoolCycle || "school_cycle")}</span>
          </Space>
        ),
      },
      {
        title: t("subjects_count"),
        dataIndex: "subjectCoefficient",
        key: "subjects_count",
        width: 160,
        render: (sc?: ClassLevel["subjectCoefficient"]) => {
          const list = (sc || []).filter((it: any) => it.optional !== true);
          const grouped = groupByBranch(list, t("base_subjects"));
          const count = grouped.reduce((sum: any, g: any) => sum + g.items.length, 0);
          return <Tag>{count}</Tag>;
        },
      },
      {
        title: t("actions"),
        key: "actions",
        width: 160,
        render: (_, record) => (
          <Space>
            <Button size="small" type="primary" ghost onClick={() => openAddModal(record)}>
              {t("add_subject")}
            </Button>
          </Space>
        ),
      },
    ],
    [openAddModal, t]
  );

  return (
       <SettingsLayout titleKey="subject_coefficients" loading={loading}>

        <Row justify="end" align="middle" className="mb-3">
          <Col xs={24} sm={12} md={8} lg={6}>
            <CommonSelect
              className="z-3 w-100"
              options={cycleOptions}
              onChange={(opt) => setSelectedCycle(opt?.value)}
              defaultValue={cycleOptions.find((o) => o.value === selectedCycle)}
            />
          </Col>
        </Row>

        {loading ? (
          <Loader />
        ) : error ? (
          <NoDataComponent />
        ) : (classLevels?.length ?? 0) === 0 ? (
          <Row justify="center" className="mt-4">
            <Alert type="info" message={t("no_class_levels_found")} showIcon />
          </Row>
        ) : (
          <>
            <Table<ClassLevel>
              rowKey="id"
              columns={columns}
              dataSource={classLevels}
              expandable={{
                expandedRowRender: (record) => (
                  <ExpandedContent record={record} onDelete={handleDelete} />
                ),
              }}
              pagination={false}
              size="middle"
            />

            <AddEditSubjectCoefficientModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              classLevelId={currentClassLevel?.id || ""}
              currentClassLevel={currentClassLevel || undefined}
              onSave={handleSave}
            />
          </>
        )}
      </SettingsLayout>
  );
};

export default SubjectCoefficientPage;
